import {
  Cartesian3,
  Cartographic,
  Color,
  createGuid,
  PolylineDashMaterialProperty,
} from "@cesium/engine";
import Utils from "../basic/Utils";
import DitapViewer from "../basic/DitapViewer";
import DitapAlert from "../popup/DitapAlert";

/**
 * 수평 거리 계산 및 영역 표시 클래스.<br>
 * Horizontal distance calculation and area display class.
 *
 * @alias MeasurementLengthHorizontal
 * @constructor
 *
 * @param {object} options 아래 속성 포함 객체.</br>Object with the following properties:
 * @param {DitapViewer} options.viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Cartesian3[]} [options.positions] 수평 거리 측정을 위한 Cartesian 좌표 배열. 지정한 경우 입력 값으로 수평 거리 계산 및 영역 표시되어 수정 불가. </br> Cartesian coordinate array for measuring horizontal distances. If specified, the input value is used for calculating the horizontal-line distance and displayed in the area, making it unmodifiable.
 * @param {Color} [options.entityColor = Color.fromBytes(66, 111, 181, 255)] 엔티티 색상.<br> Color of the entity.
 * 
 * @example
 * // 샌드캐슬 예제 'DiTAP - Horizontal Length Measurement' 참조
 * 
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * // 1. 지정된 영역 생성. (InteractionLengthHorizontal를 통한 수정 불가.)
 * const position1 = new Ditap.Cartesian3(
 *       -3044763.7129576365,
 *       4043820.120565581,
 *       3867446.689131772
 * );
 * const position2 = new Ditap.Cartesian3(
 *       -3044777.2576141134,
 *       4043794.3736621705,
 *       3867462.8381184633
 * );

 * const positions = [position1, position2];
 *
 * const options = {
 *  viewer: viewer,
 *  positions: positions,
 *  entityColor: Color.fromBytes(0, 126, 87, 255)
 * };
 * 
 * const measurementLengthHorizontal = new Ditap.MeasurementLengthHorizontal(options);
 * 
 * // 2. 마우스로 영역 지정 후 생성. (InteractionLengthHorizontal를 통해 수평 거리 측정 및 영역 표시.)
 * const measurementLengthHorizontal = new Ditap.MeasurementLengthHorizontal({
 *  viewer: viewer
 * });
 *  
 * const interactionLengthHorizontal = new Ditap.InteractionLengthHorizontal();
 * interactionLengthHorizontal.measurementLengthHorizontal = measurementLengthHorizontal;
*/
class MeasurementLengthHorizontal {
  constructor(options) {
    const viewer = options.viewer;
    const positions = options.positions;
    const entityColor = options.entityColor;

    this._id = `MeasurementLengthHorizontal${createGuid()}`;
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      viewer.ditapStorage.measurementLengthHorizontal[this.id] = this;
    } else {
      console.error("Type doesn't match");
    }
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
    } else {
      this._entityColor = Color.fromBytes(66, 111, 181, 255);
    }
    this._positions = [];
    this._centerPosition = undefined;
    this._horizontalPosition = undefined;
    this._pointEntities = [];
    this._horizontalPolylineEntity = undefined;
    this._verticalPolylineEntity = undefined;
    this._labelEntity = undefined;
    this._distance = undefined;
    this._labelHeight = undefined;
    this._windowResizeEvent = undefined;
    this._cameraChangedEvent = undefined;
    this._entityNum = 0;
    this._displayCondition = 2000;

    if (positions) {
      if (this.checkPositionsToCreate(positions)) {
        this.positions = positions;
        this.createLengthHorizontal();
      } else {
        const warningAelrt = new DitapAlert({
          type: "warning",
          title: "경고",
          content1: "세 개 이상의 좌표일 경우, 수평 거리 측정이 불가합니다.",
          buttons: "ONLYOK",
          open: false,
          callback: (alert) => {
            alert.destroy();
          },
          closeCallback: (alert) => {
            alert.destroy();
          },
        });
        warningAelrt.open();
      }
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }
  get viewer() {
    return this._viewer;
  }

  /*
   * 측정 객체 ID. </br>
   * ID to assign to the measurement object.
   * @type {string}
   */
  set id(id) {
    this._id = id;
  }
  get id() {
    return this._id;
  }

  /*
   * 수평 거리 측정 할 Cartesian 배열</br>
   * Cartesian array for measuring horizontal distances.
   * @type {Cartesian3[]}
   */
  set horizontalPosition(horizontalPosition) {
    this._horizontalPosition = horizontalPosition;
  }
  get horizontalPosition() {
    return this._horizontalPosition;
  }

  /**
   * 수평 거리 측정 할 Cartesian 배열</br>
   * Cartesian array for horizontal distances.
   * @type {Cartesian3[]}
   * @readonly
   */
  set positions(positions) {
    this._positions = positions;
  }
  get positions() {
    return this._positions;
  }

  /*
   * Polyline Entity의 중심 Cartesian 값.</br>
   * Center cartesians of the polylineEntity.
   * @type {Cartesian3}
   */
  set centerPosition(centerPosition) {
    this._centerPosition = centerPosition;
  }
  get centerPosition() {
    return this._centerPosition;
  }

  /**
   * 수평 거리 측정으로 얻은 Point Entity 배열.</br>
   * Array of point entities resulting from horizontal length measurements.
   * @type {Entity[]}
   * @readonly
   */
  set pointEntities(pointEntities) {
    this._pointEntities = pointEntities;
  }
  get pointEntities() {
    return this._pointEntities;
  }

  /**
   * 수평 거리 측정으로 얻은 수평 Polyline Entity.</br>
   * Horizontal polyline entities resulting from horizontal length measurements.
   * @type {Entity}
   * @readonly
   */
  set horizontalPolylineEntity(horizontalPolylineEntity) {
    this._horizontalPolylineEntity = horizontalPolylineEntity;
  }
  get horizontalPolylineEntity() {
    return this._horizontalPolylineEntity;
  }

  /**
   * 수평 거리 측정으로 얻은 수직 Polyline Entity.</br>
   * Vertical Polyline entities resulting from horizontal length measurements.
   * @type {Entity}
   * @readonly
   */
  set verticalPolylineEntity(verticalPolylineEntity) {
    this._verticalPolylineEntity = verticalPolylineEntity;
  }
  get verticalPolylineEntity() {
    return this._verticalPolylineEntity;
  }

  /*
   * 수평 거리 측정으로 얻은 Label Entity 배열.</br>
   * Array of label elements resulting from horizontal length measurements.
   * @type {Element}
   */
  set labelEntity(labelEntity) {
    this._labelEntity = labelEntity;
  }
  get labelEntity() {
    return this._labelEntity;
  }

  /**
   * 측정된 수평 거리 값.</br>
   * The measured horizontal distance value.
   * @type {number}
   * @readonly
   */
  set distance(distance) {
    this._distance = distance;
  }
  get distance() {
    return this._distance;
  }

  /*
   * 라벨 내부 높이. </br>
   * Internal height of the label element.
   * @type {number}
   */
  set labelHeight(labelHeight) {
    this._labelHeight = labelHeight;
  }
  get labelHeight() {
    return this._labelHeight;
  }

  /*
   * 윈도우 크기 조절 시 실행 이벤트.</br>
   * Events to run when window resizing.
   * @type {Function}
   */
  set windowResizeEvent(windowResizeEvent) {
    this._windowResizeEvent = windowResizeEvent;
  }
  get windowResizeEvent() {
    return this._windowResizeEvent;
  }

  /*
   * 카메라 시점 변경 시 실행 이벤트. </br>
   * Events to run when changing camera viewpoint in window.
   * @type {Function}
   */
  set cameraChangedEvent(cameraChangedEvent) {
    this._cameraChangedEvent = cameraChangedEvent;
  }
  get cameraChangedEvent() {
    return this._cameraChangedEvent;
  }

  /*
   * 엔티티 번호. </br>
   * Number of entities.
   * @type {number}
   */
  set entityNum(entityNum) {
    this._entityNum = entityNum;
  }
  get entityNum() {
    return this._entityNum;
  }

  /*
   * 엔티티의 DistanceDisplayCondition 값.</br>
   * DistanceDisplayCondition value of entity.
   * @type {number}
   */
  set displayCondition(displayCondition) {
    this._displayCondition = displayCondition;
  }
  get displayCondition() {
    return this._displayCondition;
  }

  /**
   * 엔티티 색상.<br>
   * Color of the entity.
   */
  set entityColor(entityColor) {
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
      Utils.changePointColor(this.pointEntities, entityColor);
      Utils.changePolylineColor(this.horizontalPolylineEntity, entityColor);
      Utils.changePolylineColor(
        this.verticalPolylineEntity,
        new PolylineDashMaterialProperty({
          color: entityColor,
        })
      );
    }
  }
  get entityColor() {
    return this._entityColor;
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    const viewer = this.viewer;
    if (this.pointEntities) {
      this.pointEntities.forEach((pointEntity) => {
        viewer.entities.remove(pointEntity);
      });
    }

    if (this.horizontalPolylineEntity) {
      viewer.entities.remove(this.horizontalPolylineEntity);
    }

    if (this.verticalPolylineEntity) {
      viewer.entities.remove(this.verticalPolylineEntity);
    }

    if (this.labelEntity) {
      this.labelEntity.remove();
    }

    if (this.labelEntity instanceof Element) {
      this.labelEntity.removeEventListener("mouseover", Utils.hideLabelElement);
      this.labelEntity.removeEventListener("mouseover", Utils.showLabelElement);
    }

    // viewerStorage에서 삭제
    this.viewer.ditapStorage.removeInstanceById(this.id);

    this.removeChangeEvent();

    this.id = undefined;
    this.positions = [];
    this.centerPosition = undefined;
    this.horizontalPosition = undefined;
    this.pointEntities = [];
    this.horizontalPolylineEntity = undefined;
    this.verticalPolylineEntity = undefined;
    this.labelEntity = undefined;
    this.distance = undefined;
    this.labelHeight = undefined;
    this.resizeObserver = undefined;
    this.cameraChangedEvent = undefined;
    this.entityNum = 0;
    this.windowResizeEvent = undefined;
  }

  /*
   * 시작 점에서 수평 좌표값 계산.</br>
   * Calculate horizontal coordinate values from the starting point.
   * @param {Cartesian3} startPosition 시작 좌표.</br> Starting cartesian.
   * @param {Cartesian3} endPosition 종료 좌표.</br> Ending cartesian.
   * @return {Cartesian3} 계산된 수평 좌표.</br> Calculated horizontal cartesian.
   */
  getHorizontalPosition(startPosition, endPosition) {
    // 시작 점에서 수평 좌표값 계산
    const start_cartographic = Cartographic.fromCartesian(startPosition);
    const mouse_cartographic = Cartographic.fromCartesian(endPosition);
    start_cartographic.height = mouse_cartographic.height;
    const horizontalPosition = Cartographic.toCartesian(start_cartographic);

    return horizontalPosition;
  }

  /*
   * 두 좌표 사이 중간 좌표 계산.</br>
   * Calculate the midpoint coordinate between two points.
   * @param {Cartesian3} startPosition 시작 좌표.</br> Starting cartesian.
   * @param {Cartesian3} endPosition 종료 좌표.</br> Ending cartesian.
   * @return {Cartesian3} 계산된 중간 좌표.</br> Calculated midpoint cartesian.
   */
  calculateLength(startPoint, endPoint) {
    return Cartesian3.distance(startPoint, endPoint).toFixed(2);
  }

  /*
   * 생성된 엔티티 색상 변경.</br>
   * Change the color of the generated entity
   * @param {Entity} entity 변경 할 엔티티.</br> Entities to change.
   * @param {Color} color 변경 할 색상.</br> Color to change.
   */
  setEntityColor(entity, color) {
    if (entity.point) {
      entity.point.color = color;
    } else if (entity.polyline) {
      entity.polyline.material = color;
    }
  }

  /*
   * Ditap Viewer에서 특정 포인트 엔티티 제거.</br>
   * Remove point entity from viewer.
   * @param {Entity} entity 제거될 엔티티.</br> entity to remove.
   */
  removePointEntity(entity) {
    const findEntity = (findentity) => findentity === entity;
    const index = this.pointEntities.findIndex(findEntity);
    this.pointEntities.splice(index, index + 1);
    this.viewer.entities.remove(entity);
  }

  /*
   * 저장된 특정 좌표 제거.</br>
   * Remove position from created position array.
   * @param {Cartesian3} position 제거될 좌표.</br> Cartesian3 to remove.
   */
  removePositions(position) {
    const findPosition = (findposition) => findposition === position;
    const index = this.positions.findIndex(findPosition);
    this.positions.splice(index, index + 1);
  }

  /*
   * 윈도우 크기 조절 이벤트 제거.</br>
   * Create the window resize event.
   */
  removeChangeEvent() {
    if (this.cameraChangedEvent) {
      this.cameraChangedEvent();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.windowResizeEvent) {
      window.removeEventListener("resize", this.windowResizeEvent);
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createWindowResizeEvent() {
    const viewer = this.viewer;
    const container = this.viewer.container;
    const labelEntity = this.labelEntity;
    const positions = this.centerPosition;
    const height = this.labelHeight;
    const displayCondition = this.displayCondition;

    const resizeOriginal = () => {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntity,
        positions,
        displayCondition,
        height
      );
    };

    // ResizeObserver 객체를 생성하고 콜백 함수 전달.
    this.resizeObserver = new ResizeObserver(resizeOriginal);

    // 대상 요소에 대한 크기 변화 관찰.
    this.resizeObserver.observe(container);

    this.windowResizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.windowResizeEvent);
  }

  /*
   * 카메라 시점 이동 시 이벤트 생성. </br>
   * Create an event when moving the camera point of view.
   */
  createCameraMoveEvent() {
    const viewer = this.viewer;
    const labelEntity = this.labelEntity;
    const positions = this.centerPosition;
    const height = this.labelHeight;
    const displayCondition = this.displayCondition;

    // 카메라 이동 이벤트 생성
    this.cameraChangedEvent = viewer.camera.changed.addEventListener(() => {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntity,
        positions,
        displayCondition,
        height
      );
    });
  }

  /*
   * 수평 거리 측정 가능 좌표인지 구분.</br>
   * Determine if the coordinates are suitable for measuring horizontal distance.
   * @param {Cartesian3[]} positions 측정할 좌표 배열.<br> Array of Cartesian3 to measure.
   * @returns {boolean} 구분 결과.</br> result.
   */
  checkPositionsToCreate(positions) {
    if (
      positions.length === 2 &&
      positions[0] instanceof Cartesian3 &&
      positions[1] instanceof Cartesian3 &&
      positions[0] !== positions[1]
    ) {
      return true;
    }
    return false;
  }

  /*
   * Ditap Viewer에서 MeasurementLengthHorizontal 엔티티 생성.</br>
   * Create MeasurementLengthHorizontal Entities in the Ditap Viewer.
   */
  createLengthHorizontal() {
    const displayCondition = Utils.getDistanceDisplayConditon(
      this.positions,
      this.viewer
    );

    if (displayCondition > 2000) {
      this.displayCondition = displayCondition;
    }

    const startPosition = this.positions[0];
    const endPosition = this.positions[1];

    this.horizontalPosition = this.getHorizontalPosition(
      startPosition,
      endPosition
    );
    this.distance = this.calculateLength(this.horizontalPosition, endPosition);

    // POINT - 시작 지점에 POINT 엔티티 생성
    const startPointEntity = Utils.createPoint({
      viewer: this.viewer,
      id: this.id + this.entityNum,
      position: startPosition,
      outlineColor: this.entityColor,
      displayCondition: this.displayCondition,
    });
    this.entityNum++;

    // POINT - 수직 지점에 POINT 엔티티 생성
    const horizontalPointEntity = Utils.createPoint({
      viewer: this.viewer,
      id: this.id + this.entityNum,
      position: this.horizontalPosition,
      outlineColor: this.entityColor,
      displayCondition: this.displayCondition,
    });
    this.entityNum++;

    // POINT - 종료 지점에 POINT 엔티티 생성
    const endPointEntity = Utils.createPoint({
      viewer: this.viewer,
      id: this.id + this.entityNum,
      position: endPosition,
      outlineColor: this.entityColor,
      displayCondition: this.displayCondition,
    });
    this.entityNum++;

    // 엔티티 저장
    this.pointEntities.push(startPointEntity);
    this.pointEntities.push(endPointEntity);
    this.pointEntities.push(horizontalPointEntity);

    // POLYLINE - 수직거리 수직 POLYLIN 엔티티 생성
    this.verticalPolylineEntity = Utils.createPolyline({
      viewer: this.viewer,
      id: this.id + this.entityNum,
      positions: [startPosition, this.horizontalPosition],
      material: new PolylineDashMaterialProperty({
        color: this.entityColor,
      }),
      depthFailMaterial: new PolylineDashMaterialProperty({
        color: this.entityColor.withAlpha(0.5),
      }),
      displayCondition: this.displayCondition,
    });
    this.entityNum++;

    // POLYLINE - 수직거리 수평POLYLIN 엔티티 생성
    this.horizontalPolylineEntity = Utils.createPolyline({
      viewer: this.viewer,
      id: this.id + this.entityNum,
      positions: [this.horizontalPosition, endPosition],
      material: this.entityColor,
      displayCondition: this.displayCondition,
    });
    this.entityNum++;

    // LABEL - 엔티티 생성
    this.labelEntity = Utils.createTotalLabel(this.viewer, this.distance);
    this.labelHeight = this.labelEntity.clientHeight;

    this.centerPosition = Cartesian3.midpoint(
      this.horizontalPosition,
      endPosition,
      new Cartesian3()
    );

    Utils.changeLabelLocation(
      this.viewer,
      this.centerPosition,
      this.labelEntity,
      this.labelHeight
    );

    this.createWindowResizeEvent();
    this.createCameraMoveEvent();
  }
}
export default MeasurementLengthHorizontal;
